import React, { useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { enrollMFA, verifyMFA } from "../services/mfaService";
import { getSupabaseClient } from "../services/supabaseClient";

export default function MFAEnrollment({ userId, onEnrolled, onCancel }) {
    const [enrollment, setEnrollment] = useState(null);
    const [verificationCode, setVerificationCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState([]);

    const qrCodeValue = useMemo(() => {
        return (
            enrollment?.totp?.qr_code ||
            enrollment?.totp?.uri ||
            enrollment?.totp?.qrCode ||
            ""
        );
    }, [enrollment]);

    const manualCode = useMemo(() => {
        return enrollment?.totp?.secret || enrollment?.totp?.secretKey || "";
    }, [enrollment]);

    const handleEnroll = async () => {
        setError(null);
        setSubmitting(true);
        try {
            const data = await enrollMFA();
            setEnrollment(data);
        } catch (err) {
            setError(err?.message || "Unable to enroll MFA.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerify = async () => {
        if (!enrollment?.id) {
            setError("Please start enrollment first.");
            return;
        }

        const cleaned = verificationCode.replace(/\s+/g, "");
        if (!/^\d{6}$/.test(cleaned)) {
            setError("Enter the 6-digit code from your authenticator app.");
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const data = await verifyMFA({
                factorId: enrollment.id,
                code: cleaned,
            });

            const supabase = getSupabaseClient();
            const { error: profileError } = await supabase
                .from("profiles")
                .update({
                    mfa_enabled: true,
                    mfa_enrolled_at: new Date().toISOString(),
                })
                .eq("id", userId);

            if (profileError) {
                throw profileError;
            }

            const codes =
                data?.recovery_codes ||
                data?.recoveryCodes ||
                data?.totp?.recovery_codes ||
                [];
            setRecoveryCodes(Array.isArray(codes) ? codes : []);
            setSuccess(true);
            onEnrolled?.();
        } catch (err) {
            setError(err?.message || "Unable to verify MFA.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mfa-enrollment">
            <h3>Enable Two-Factor Authentication</h3>
            <p>
                Scan the QR code with your authenticator app, then enter the 6-digit
                code to confirm.
            </p>

            {!enrollment && (
                <button
                    className="btn primary"
                    type="button"
                    onClick={handleEnroll}
                    disabled={submitting}
                >
                    {submitting ? "Starting..." : "Enable MFA"}
                </button>
            )}

            {enrollment && (
                <div className="mfa-enrollment-details">
                    {qrCodeValue ? (
                        <div className="qr-code">
                            <QRCodeCanvas value={qrCodeValue} size={160} />
                        </div>
                    ) : (
                        <p className="warning-message">QR code unavailable.</p>
                    )}
                    {manualCode && (
                        <div className="manual-code">
                            <span>Manual entry code</span>
                            <code>{manualCode}</code>
                        </div>
                    )}
                    <label className="form-field">
                        <span>Verification code</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="123456"
                        />
                    </label>
                    <button
                        className="btn primary"
                        type="button"
                        onClick={handleVerify}
                        disabled={submitting}
                    >
                        {submitting ? "Verifying..." : "Verify and finish"}
                    </button>
                </div>
            )}

            {success && (
                <div className="mfa-success">
                    <p>MFA is enabled for your account.</p>
                    {recoveryCodes.length > 0 && (
                        <div className="recovery-codes">
                            <h4>Recovery codes</h4>
                            <p>
                                Store these recovery codes somewhere safe. Each code can
                                be used once.
                            </p>
                            <ul>
                                {recoveryCodes.map((code) => (
                                    <li key={code}>{code}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {error && <p className="error-message">{error}</p>}

            {onCancel && (
                <button className="btn ghost" type="button" onClick={onCancel}>
                    Cancel
                </button>
            )}
        </div>
    );
}
